import React, { useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { COLORS, categoryIconMap } from "../../constants";

/**
 * Checkbox component for the web platform.
 *
 * @param {Object} props - Props for the checkbox component.
 * @param {string} props.label - Label text for the checkbox.
 * @param {boolean} props.value - Checked state of the checkbox.
 * @param {Function} props.onChange - Callback for value change.
 * @param {string} props.color - Checked color.
 * @param {string} props.uncheckedColor - Unchecked color.
 * @returns {JSX.Element} A web-specific checkbox component.
 */
const CheckboxWeb = ({ label, value = false, onChange, color = COLORS.primary, uncheckedColor = COLORS.border }) => {
  const id = useId();

  return (
    <div style={styles.container}>
      <input 
        id={id} 
        type="checkbox" 
        checked={value} 
        onChange={() => onChange(label)}
        style={styles.hiddenInput}
        aria-label={`${label} checkbox`}
        aria-checked={value}
      />
      <label
        htmlFor={id}
        style={{
          ...styles.checkboxLabel,
          ...styles.checkbox,
          backgroundColor: value ? color : "transparent",
          borderColor: value ? color : uncheckedColor,
        }}
        aria-hidden="true"
      >
        {value && <FontAwesomeIcon icon={faCheck} style={styles.checkmark} />}
      </label>
      {label && categoryIconMap[label] && (
        <FontAwesomeIcon 
          icon={categoryIconMap[label]} 
          size="lg" 
          color={COLORS.border} 
          style={styles.categoryIcon}
          aria-hidden="true"
        />
      )}
      {label && (
        <span
          onClick={() => onChange(label)}
          style={{
            ...styles.label,
            userSelect: "none",
          }}
          aria-hidden="true"
        >
          {label}
        </span>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  hiddenInput: {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: "none",
  },
  checkboxLabel: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  checkbox: {
    width: 20,
    height: 20,
    border: "2px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: 4,
    transition: "all 0.2s ease-in-out",
    flexShrink: 0,
  },
  categoryIcon: {
    marginLeft: 12,
    marginRight: 2,
  },
  checkmark: {
    color: COLORS.textLight,
    fontSize: 14,
  },
  label: {
    marginLeft: 8,
    fontSize: 16,
    color: COLORS.textDark,
    cursor: "pointer",
  },
};

export default CheckboxWeb;
